import { forwardRef, useId, type SelectHTMLAttributes } from 'react';

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  message?: string | undefined;
  invalid?: boolean;
}

/** Labelled, accessible select input — mirrors the TextField API for consistent use in forms. */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, message, invalid = false, required, children, className, ...rest },
  ref,
) {
  const inputId = useId();
  const messageId = `${inputId}-message`;

  return (
    <div className="zs-field">
      <label className="zs-field__label" htmlFor={inputId}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <select
        id={inputId}
        ref={ref}
        className={['zs-field__input', className].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        aria-describedby={message ? messageId : undefined}
        aria-required={required || undefined}
        required={required}
        {...rest}
      >
        {children}
      </select>
      {message ? (
        <span
          id={messageId}
          className={invalid ? 'zs-field__error' : 'zs-field__label'}
          role={invalid ? 'alert' : undefined}
        >
          {message}
        </span>
      ) : null}
    </div>
  );
});
